import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { type Address, maxUint256 } from "viem";
import { ERC20_ABI } from "../config/contracts";
import { useState, useCallback, useEffect } from "react";

export function useTokenAllowance(
  tokenAddress: Address | undefined,
  spender: Address | undefined,
  requiredAmount: bigint = 0n
) {
  const { address: account } = useAccount();
  const [approvalTxHash, setApprovalTxHash] = useState<`0x${string}` | undefined>();

  const isNative =
    !tokenAddress || tokenAddress === "0x0000000000000000000000000000000000000000";

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "allowance",
    args: account && spender ? [account, spender] : undefined,
    query: {
      enabled: !!account && !!spender && !isNative && !!tokenAddress,
      refetchInterval: 6_000,
    },
  });

  const { writeContract, isPending: isApproving } = useWriteContract();

  const { isLoading: isWaitingForApproval, isSuccess: approvalConfirmed } =
    useWaitForTransactionReceipt({
      hash: approvalTxHash,
    });

  useEffect(() => {
    if (approvalConfirmed) {
      refetchAllowance();
      setApprovalTxHash(undefined);
    }
  }, [approvalConfirmed, refetchAllowance]);

  const currentAllowance = (allowance as bigint) ?? 0n;
  const needsApproval = !isNative && requiredAmount > 0n && currentAllowance < requiredAmount;

  const approve = useCallback(() => {
    if (!tokenAddress || !spender || isNative) return;

    writeContract(
      {
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [spender, maxUint256],
      },
      {
        onSuccess(hash) {
          setApprovalTxHash(hash);
        },
      }
    );
  }, [tokenAddress, spender, isNative, writeContract]);

  return {
    allowance: currentAllowance,
    needsApproval,
    approve,
    isApproving: isApproving || isWaitingForApproval,
    refetchAllowance,
  };
}
